import React, {Fragment, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Select from 'react-select'
import {
    addProduct,
    clearErrors,
    getUnitOptions,
    getMaterialOptions,
    getWarehouseOptions
} from "../../actions/product";
import {Accordion} from "react-bootstrap";
import {ArrowLeft, Plus, X} from "react-feather";
import {Multiselect} from "multiselect-react-dropdown";
import {formatMoney, getParams, getSelectedOption} from "../../utils/hooks/getParams";
import {getFirmOptionsForManagers} from "../../actions/user";
import {isArray} from "chart.js/helpers";
import {Input} from "reactstrap";
import ButtonLoader from "../layout/ButtonLoader";
import useActiveFirm from '../../hooks/useActiveFirm';

const ProductCreateForm = ({
                               unit_options,
                               firm_options,
                               products,
                               loading,
                               error,
                               warehouse_options,
                               material_options,
                               handleClose,
                               show,
                               getUnitOptions,
                               addProduct,
                               getMaterialOptions,
                               getWarehouseOptions,
                               getFirmOptionsForManagers,
                               currentPage,
                           }) => {
    const { firmId } = useActiveFirm();

    useEffect(() => {
        getUnitOptions();
        getFirmOptionsForManagers();
    }, [getUnitOptions, getMaterialOptions, getFirmOptionsForManagers]);

    const formRef = useRef();

    const defaultValues = {
        name: '',
        description: '',
        unit_id: '',
        warehouse_id: '',
        is_listed_in_report: true,
        is_listed_in_products_table: true,
        materials: []
    };


    const [firmOptions, setFirmOptions] = useState([]);
    const [warehouseOptions, setWarehouseOptions] = useState([]);

    useEffect(() => {
        let items = [];
        firm_options.forEach((item) => items.push({
            label: `${item.name}`,
            value: item.id
        }));
        setFirmOptions(items);

    }, [firm_options]);


    useEffect(() => {
        let options = [];
        warehouse_options.forEach((item) => options.push({
            label: item.name,
            value: item.id
        }));
        setWarehouseOptions(options);
    }, [warehouse_options]);

    const [inputs, setInputs] = useState(defaultValues);
    const [errors, setErrors] = useState({});

    // Auto-populate with global firm when modal opens or firm changes
    useEffect(() => {
        if (show && firmId) {
            setInputs(prev => ({ ...prev, firm_id: firmId }));
            getWarehouseOptions({ firm_id: firmId });
            getMaterialOptions({ firm_id: firmId });
        }
    }, [show, firmId, getWarehouseOptions, getMaterialOptions]);
    
    // Ensure firm_id is always set from global active firm
    useEffect(() => {
        if (firmId && inputs.firm_id !== firmId) {
            setInputs(prev => ({ ...prev, firm_id: firmId }));
            if (show) {
                getWarehouseOptions({ firm_id: firmId });
                getMaterialOptions({ firm_id: firmId });
            }
        }
    }, [firmId, show, getWarehouseOptions, getMaterialOptions, inputs.firm_id]);

    const handleChange = e => {
        setErrors({});
        e.target.oldValue = e.target.value;
        if (e.target.name === 'is_listed_in_report' || e.target.name === 'is_listed_in_products_table'){
            setInputs(prevState => ({...prevState, [e.target.name]: !inputs[e.target.name]}));
        }else{
            setInputs(prevState => ({...prevState, [e.target.name]: e.target.value}));
        }
    };

    useEffect(() => {
        clearErrors();
        setErrors({});
        setInputs(defaultValues);

    }, [products]);

    const onClose = () => {
        clearErrors();
        setInputs(defaultValues);
        setErrors({});
        handleClose();
    };
    useEffect(() => {
        material_options.map(item => {
            item.unit_value = 0;
            return item;
        });
        setMaterialOptions(material_options);
    }, [material_options]);


    const [materialOptions, setMaterialOptions] = useState(material_options);
    const [selectedDynamicMaterialOptions, setSelectedDynamicMaterialOptions] = useState([]);
    const [selectedMaterialOptions, setSelectedMaterialOptions] = useState([]);

    const onSelectMaterial = (data) => {
        setSelectedDynamicMaterialOptions(data);
    };

    const onRemoveMaterial = (data) => {
        setSelectedDynamicMaterialOptions(data)
    };


    const onClickMoveSelectedMaterials = () => {
        if (selectedDynamicMaterialOptions.length > 0) {
            setSelectedMaterialOptions([...selectedMaterialOptions, ...selectedDynamicMaterialOptions]);
            let ids = selectedDynamicMaterialOptions.map(({id}) => id);
            const filteredMaterials = materialOptions.filter((item) => !ids.includes(item.id));
            setMaterialOptions(filteredMaterials);
            setSelectedDynamicMaterialOptions([]);
        }
    };

    const onChangeInput = (e) => {
        let material_id = parseInt(e.target.dataset.id);
        const cleanItems = [];

        const items = selectedMaterialOptions.map((item) => {
            if (item.id === material_id) {
                item.unit_value = e.target.value;
            }
            cleanItems.push({
                id: item.id,
                unit_value: parseFloat(item.unit_value) || 0
            });
            return item;
        });
        setSelectedMaterialOptions(items);
        setErrors({});
        setInputs(prevState => ({...prevState, materials: cleanItems}));
    };


    const onClickRemoveFromSelectedMaterials = (id) => {
        const filteredSelectedMaterials = selectedMaterialOptions.filter(item => item.id !== Number(id));
        setSelectedMaterialOptions([...filteredSelectedMaterials]);
        let ids = filteredSelectedMaterials.map(({id}) => id);
        const filteredMaterials = material_options.filter((item) => !ids.includes(item.id));

        const cleanItems = inputs.materials;
        const filteredItems = cleanItems.filter((item) => item.id !== id);
        setInputs(prevState => ({...prevState, materials: [...filteredItems]}));
        setMaterialOptions(filteredMaterials);
        setSelectedDynamicMaterialOptions([]);
    }

    const handleSelect = (data, type) => {
        setErrors({});
        // Firm selection is handled by global firm switcher, not here
        setInputs(prevState => ({...prevState, [type.name]: data.value}))
    };


    const handleSubmit = async (event) => {
        event.preventDefault();
        const data = {
            name: inputs.name,
            is_listed_in_report: inputs.is_listed_in_report,
            is_listed_in_products_table: inputs.is_listed_in_products_table,
            warehouse_id: inputs.warehouse_id,
            unit_id: inputs.unit_id,
            description: inputs.description,
            materials: inputs.materials,
            firm_id: inputs.firm_id ? Number(inputs.firm_id) : undefined
        };
        const params = getParams(true);
        // Always include firm_id from global active firm
        if (firmId) {
            params.firm_id = firmId;
        }
        // Include current page if available
        if (currentPage) {
            params.page = currentPage;
        }
        const is_added = await addProduct(data, params);
        if (is_added){
            onClose()
        }

    };

    useEffect(() => {
        if (loading === false) {
            if (error) {
                const errorsItems = error?.errors;
                let errorObject = {};
                if (error?.errors) {
                    if (error && error?.errors) {
                        Object.keys(error?.errors).forEach((item) => errorObject[item] = errorsItems[item][0]);
                    }
                    setErrors(errorObject);
                }

            }
        }
    }, [error, loading]);


    useEffect(() => {
        setSelectedDynamicMaterialOptions([]);
        setSelectedMaterialOptions([]);
        setMaterialOptions([]);
        setMaterialOptions(material_options);
    }, [products])


    return (
        <Fragment>
            <Modal size="lg" show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Yangi mahsulot qo'shish</Modal.Title>
                </Modal.Header>

                <Modal.Body className="pt-3 pb-4 mx-3">

                    <form onSubmit={handleSubmit} ref={formRef}>

                        <div className="mb-3">
                            <label htmlFor="name">Mahsulot nomi</label>
                            <input
                                name="name"
                                className={'form-control'}
                                type="text"
                                value={inputs?.name || ''}
                                placeholder={'kiriting'}
                                onChange={handleChange}
                                onKeyUp={e => e.target.oldValue = e.target.value}
                            />

                            <div className="error">
                                {errors?.name}
                            </div>
                        </div>


                        <div className="mb-3">
                            <label htmlFor="firm_id">Sklad</label>
                            <Select
                                name={'warehouse_id'}
                                options={warehouseOptions}
                                value={getSelectedOption(inputs?.warehouse_id, warehouseOptions)}
                                onChange={handleSelect}
                                defaultValue={{label: 'Tanlang', value: ''}}
                            />
                            <div className="error">
                                {errors?.warehouse_id}
                            </div>
                        </div>

                        <div className="mb-3">
                            <div className="row materials__mover">
                                <div className="col-lg-5 col-md-12">
                                    <label className="form-label mb-2">Tarkibi</label>

                                    <div className="card p-2">
                                        <Accordion>
                                            {
                                                isArray(selectedMaterialOptions) && selectedMaterialOptions.length > 0 ? (
                                                    selectedMaterialOptions.map((item, index) => (
                                                        <Accordion.Item key={index} eventKey={item.id}>
                                                            <Accordion.Header>
                                                                {item.name} ({parseFloat(item?.in_stock).toFixed(2)} {item.unit?.symbol})
                                                            </Accordion.Header>
                                                            <Accordion.Body>
                                                                <div className="form-group">
                                                                    <div className="input-group mb-1 justify-content-between">
                                                                        <label htmlFor="ID">Hozirgi tan narxi</label>
                                                                        <X className={'pointered-icon'}
                                                                           onClick={() => onClickRemoveFromSelectedMaterials(item.id)}
                                                                           color={'red'}
                                                                        />
                                                                    </div>
                                                                    <input
                                                                        className="form-control my-2 float-right"
                                                                        type="text"
                                                                        name="id"
                                                                        id="net_price"
                                                                        value={formatMoney(parseFloat(item.net_price).toFixed(2))}
                                                                        required
                                                                        disabled
                                                                    />
                                                                    <div className="input-group mb-3">
                                                                        <input 
                                                                            className="form-control"
                                                                            placeholder="Miqdori"
                                                                            name='unit_value'
                                                                            type="number"
                                                                            step="0.000001"
                                                                            min="0"
                                                                            value={item.unit_value || ''}
                                                                            data-id={item.id}
                                                                            id={`unit_value${item.id}`}
                                                                            required
                                                                            onKeyPress={(e) => {
                                                                                if (!/[0-9.]/.test(e.key)) {
                                                                                    e.preventDefault();
                                                                                }
                                                                            }}
                                                                            onChange={(e) => {
                                                                                const value = e.target.value.replace(/[^0-9.]/g, '');
                                                                                // Ensure only one decimal point
                                                                                const parts = value.split('.');
                                                                                const sanitizedValue = parts.length > 2 
                                                                                    ? `${parts[0]}.${parts.slice(1).join('')}`
                                                                                    : value;
                                                                                onChangeInput({
                                                                                    target: {
                                                                                        value: sanitizedValue,
                                                                                        dataset: {
                                                                                            id: item.id
                                                                                        }
                                                                                    }
                                                                                });
                                                                            }}
                                                                        />
                                                                        <span className="input-group-text" id="unit_value">
                                                                            {item.unit_alternative?.symbol}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </Accordion.Body>
                                                        </Accordion.Item>
                                                    ))
                                                ) : (
                                                    <h6 className="text-secondary">
                                                        O'ng tomondan material tanlab qo'shing!
                                                    </h6>
                                                )
                                            }
                                        </Accordion>

                                    </div>

                                    <div className="error">
                                        {errors?.materials}
                                    </div>

                                </div>

                                <div className="col-md-12 justify-content-center col-lg-2 text-center">
                                <br/>
                                    <br/>
                                    <button onClick={onClickMoveSelectedMaterials} type={'button'}
                                            className="btn-primary py-2 px-3">
                                        <ArrowLeft/>
                                    </button>
                                </div>

                                <div className="col-lg-5 col-md-12">

                                    <div className="mt-2">
                                        <label htmlFor="" className="mb-2">Materiallar</label>
                                        <Multiselect
                                            className="form-control mt-2"
                                            options={materialOptions}
                                            displayValue="name"
                                            onSelect={onSelectMaterial}
                                            onRemove={onRemoveMaterial}
                                            selectedValues={selectedDynamicMaterialOptions}
                                            placeholder="Materiallarni tanlang"
                                        />

                                    </div>

                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label htmlFor="exampleFormControlInput1" className="form-label">Qisqa
                                ma'lumot </label>
                            <textarea className="form-control" value={inputs?.description}
                                      onChange={handleChange} name="description"
                                      id="description"
                                      rows="4"></textarea>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="unit_id" className="form-label">
                                Mahsulot sarflash o'lchov birligi
                            </label>
                            <select className="form-control" value={inputs?.unit_id}
                                    onChange={handleChange} name="unit_id"
                                    id="unit_id">
                                <option defaultValue={''}>Tanlang</option>
                                {
                                    unit_options && unit_options.map((item, key) => (
                                        <option key={key}
                                                defaultValue={key === 0}
                                                value={item.id}>{item.name}</option>
                                    ))
                                }
                            </select>

                            <div className="error">
                                {errors?.unit_id}
                            </div>
                        </div>



                        <div className="mb-3 form-check">
                            <Input
                                type="checkbox"
                                id={'is_listed_in_report'}
                                value={inputs.is_listed_in_report}
                                checked={inputs.is_listed_in_report}
                                name={"is_listed_in_report"}
                                onChange={handleChange}
                            />
                            <label htmlFor="is_listed_in_report" className="form-label">
                                Otchet jadvalida ko'rsatilsin
                            </label>
                        </div>

                        <div className="mb-3 form-check">
                            <Input
                                type="checkbox"
                                id={'is_listed_in_products_table'}
                                value={inputs?.is_listed_in_products_table}
                                checked={inputs.is_listed_in_products_table}
                                name={"is_listed_in_products_table"}
                                onChange={handleChange}
                            />
                            <label htmlFor="is_listed_in_products_table" className="form-label pl-4 "
                                   onClick={() => setInputs({...inputs, is_listed_in_products_table: !inputs.is_listed_in_products_table})}>
                                Elektron Mahsulotlar jadvalida ko'rinsin
                            </label>
                        </div>


                        <div className="d-flex gap-2">
                            <Button className="w-50" variant="secondary" type="button" onClick={onClose}>Bekor
                                qilish</Button>
                            <Button className="w-50" variant="primary" type="submit">
                                <Plus/> Qo'shish
                                {loading && <ButtonLoader/>}
                            </Button>
                        </div>

                    </form>

                </Modal.Body>

            </Modal>
        </Fragment>
    );
};

ProductCreateForm.propTypes = {
    addProduct: PropTypes.func.isRequired,
    getUnitOptions: PropTypes.func.isRequired,
    getMaterialOptions: PropTypes.func.isRequired,
    getWarehouseOptions: PropTypes.func.isRequired,
    getFirmOptionsForManagers: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
    auth: state.auth,
    product: state.product,
    unit_options: state.product.unit_options,
    firm_options: state.user.firm_options,
    warehouse_options: state.product.warehouse_options,
    material_options: state.product.material_options,
    products: state.product.products,
    loading: state.product.loading,
    error: state.product.error,
    currentPage: state.product.currentPage,
});


export default connect(mapStateToProps, {
    addProduct,
    clearErrors,
    getUnitOptions,
    getMaterialOptions,
    getWarehouseOptions,
    getFirmOptionsForManagers
})(ProductCreateForm);
